import React, { useState, useEffect } from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import AgenticTopBar from '../TopBar/AgenticTopBar';
import AgenticSidebar from '../Sidebar/AgenticSidebar';
import AgenticLandingContent from '../LandingPage/AgenticLandingContent';
import { AgenticMainLayoutProps, DEFAULT_BREADCRUMBS, DEFAULT_QUICK_ACTIONS } from '../AskAsperAgentic.types';

/**
 * AgenticMainLayout - Main container layout combining TopBar, Sidebar, and Content
 * Part of the AskAsper Agentic Platform components
 *
 * This component provides a complete landing page layout with:
 * - Top navigation bar with breadcrumbs (Menu icon toggles sidebar drawer)
 * - Collapsible sidebar drawer (opens from the side via Menu icon, flex layout)
 * - Gradient background effect
 * - Center content area (landing or custom content via children)
 */
const AgenticMainLayout: React.FC<AgenticMainLayoutProps> = ({
    // Layout configuration
    showTopBar = true,
    disableNavbar = false,
    footerHeight = 0,

    // User info
    userName = 'User',

    // Sidebar - threads and related
    threads = [],
    threadsLoading = false,
    activeThreadId,
    user: sidebarUser,
    sidebarDefaultCollapsed = false,
    sidebarHideHeader = false,

    // Breadcrumb
    breadcrumbs = DEFAULT_BREADCRUMBS,

    // Quick actions
    quickActions = DEFAULT_QUICK_ACTIONS,

    // Search state
    searchValue: controlledSearchValue,
    isLoading = false,

    // Event handlers
    onNewChat,
    onHistoryClick,
    onNotificationClick,
    onBreadcrumbClick,
    onHomeClick,
    onSearchChange,
    onSearchSubmit,
    onQuickActionClick,
    onQuestionBankClick,
    onSaveAsCustom,
    questionBankCategories,
    customQuestions,
    onEditCustomQuestion,
    onDeleteCustomQuestion,
    onExportClick,
    onThreadSelect,
    onThreadEdit,
    onThreadDelete,
    onUserClick,

    // Children for custom content
    children,

    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();

    // Internal state for uncontrolled mode
    const [internalSearchValue, setInternalSearchValue] = useState('');
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Use controlled or uncontrolled values
    const searchValue = controlledSearchValue !== undefined ? controlledSearchValue : internalSearchValue;

    // Handlers
    const handleMenuToggle = () => {
        setSidebarOpen((prev) => !prev);
    };

    const handleSidebarClose = () => {
        setSidebarOpen(false);
    };

    // Close sidebar on Escape
    useEffect(() => {
        if (!sidebarOpen) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') handleSidebarClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [sidebarOpen]);

    const handleSearchChange = (value: string) => {
        if (onSearchChange) {
            onSearchChange(value);
        } else {
            setInternalSearchValue(value);
        }
    };

    return (
        <ConfigProvider theme={theme}>
            <div
                className="bg-white flex flex-col w-full overflow-hidden"
                style={
                    footerHeight != null && footerHeight > 0
                        ? { height: `calc(100vh - ${footerHeight}px)` }
                        : { height: '100vh' }
                }
                data-test={dataTest}
                data-test-id={dataTestId}
                data-agentic-main-layout
            >
                {/* Top Bar - hidden when disableNavbar (host app has its own navbar) */}
                {showTopBar && !disableNavbar && (
                    <AgenticTopBar
                        breadcrumbs={breadcrumbs}
                        onBreadcrumbClick={onBreadcrumbClick}
                        onHomeClick={onHomeClick}
                        onNotificationClick={onNotificationClick}
                        onMenuToggle={handleMenuToggle}
                        showCreateCaseButton={false}
                        showViewModeToggle={false}
                        onExportClick={onExportClick}
                        data-test-id={`${dataTestId}-top-bar`}
                    />
                )}

                {/* Main Content Area - flex layout with sidebar drawer */}
                <div className="flex flex-1 overflow-hidden">
                    {/* Sidebar Drawer - opens from the side via Menu icon */}
                    <div
                        className="flex shrink-0 h-full overflow-hidden transition-[width] duration-200 ease-out"
                        style={{ width: sidebarOpen ? 244 : 0 }}
                    >
                        {sidebarOpen && (
                            <AgenticSidebar
                                threads={threads}
                                threadsLoading={threadsLoading}
                                activeThreadId={activeThreadId}
                                user={sidebarUser ?? { name: userName }}
                                defaultCollapsed={sidebarDefaultCollapsed}
                                hideHeader={sidebarHideHeader}
                                onNewChat={onNewChat}
                                onSearchChats={onHistoryClick}
                                onThreadSelect={onThreadSelect}
                                onThreadEdit={onThreadEdit}
                                onThreadDelete={onThreadDelete}
                                onToggleSidebar={handleSidebarClose}
                                onUserClick={onUserClick}
                                data-test-id={`${dataTestId}-sidebar`}
                            />
                        )}
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 min-w-0 relative overflow-auto">
                        {/* Background Gradient Effect */}
                        <div className="absolute inset-0 pointer-events-none overflow-hidden">
                            {/* Top-right gradient blob */}
                            <div
                                className="absolute top-0 right-0 w-[600px] h-[500px] opacity-70"
                                style={{
                                    background:
                                        'radial-gradient(ellipse at center, rgba(167,139,250,0.8) 0%, rgba(147,197,253,0.5) 40%, transparent 70%)',
                                    filter: 'blur(60px)',
                                    transform: 'translate(10%, -10%)',
                                }}
                            />
                            {/* Bottom-left gradient blob */}
                            <div
                                className="absolute bottom-0 left-0 w-[500px] h-[400px] opacity-80"
                                style={{
                                    background:
                                        'radial-gradient(ellipse at center, rgba(167,139,250,0.5) 0%, rgba(196,181,253,0.4) 50%, transparent 70%)',
                                    filter: 'blur(60px)',
                                    transform: 'translate(-20%, 20%)',
                                }}
                            />

                            {/* Background Gradient Effect - Diagonal linear gradient from top-right (light purple) to bottom-left (white) */}
                            <div
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                    background:
                                        'linear-gradient(240deg, rgba(224,217,255,0.6) 0%, rgba(240,235,255,0.3) 40%, rgba(250,248,255,0.1) 60%, rgba(255,255,255,0) 100%)',
                                }}
                            />
                        </div>

                        {/* Content */}
                        <div className="relative z-10 h-full flex items-center justify-center p-4">
                            {children || (
                                <AgenticLandingContent
                                    userName={userName}
                                    quickActions={quickActions}
                                    searchValue={searchValue}
                                    onSearchChange={handleSearchChange}
                                    onSearchSubmit={onSearchSubmit}
                                    onQuickActionClick={onQuickActionClick}
                                    onQuestionBankClick={onQuestionBankClick}
                                    onSaveAsCustom={onSaveAsCustom}
                                    questionBankCategories={questionBankCategories}
                                    customQuestions={customQuestions}
                                    onEditCustomQuestion={onEditCustomQuestion}
                                    onDeleteCustomQuestion={onDeleteCustomQuestion}
                                    isLoading={isLoading}
                                    showSidebarToggleInPanel={disableNavbar}
                                    onSidebarToggleInPanel={disableNavbar ? handleMenuToggle : undefined}
                                    data-test-id={`${dataTestId}-landing-content`}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </ConfigProvider>
    );
};

export default AgenticMainLayout;
